import { Field, FieldProps, Form, Formik, FormikErrors, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { FormikPoolDatePicker, PoolButton, PoolModal, PoolTextArea, PoolTextField } from '@synergy/components';
import { Grid, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useRef } from 'react';

import { BankAccountFormValues } from '../../models/bank-account-form-values';
import { BankAccountsDocuments } from './bank-accounts-documents';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(1.5), // FIXME: fix PoolModal container styling
    },
}));

interface BankAccountsModalProps {
    bankAccount: BankAccountFormValues | null;
    errors?: FormikErrors<BankAccountFormValues>;
    onClose: () => void;
    onBankAccountSubmit: (values: BankAccountFormValues) => void;
}

export const BankAccountsModal = (props: BankAccountsModalProps) => {
    const { bankAccount, onClose, onBankAccountSubmit, errors } = props;
    const classes = useStyles();
    const formikRef = useRef<FormikProps<BankAccountFormValues>>(null);

    useEffect(() => {
        if (!formikRef.current) {
            return;
        }
        formikRef.current.setErrors(errors ?? {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors]);

    const actionChildren = [
        <PoolButton key="1" styleType="firstLevel" variant="text" onClick={onClose}>
            <FormattedMessage id="app.common.cancel" />
        </PoolButton>,
        <Form key="2">
            <PoolButton styleType="firstLevel" type="submit">
                <FormattedMessage id={bankAccount?.id ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>
        </Form>,
    ];

    if (!bankAccount) {
        return null;
    }

    return (
        <Formik
            initialValues={bankAccount}
            onSubmit={onBankAccountSubmit}
            enableReinitialize={true}
            innerRef={formikRef}
        >
            {formikProps => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={
                                    bankAccount?.id
                                        ? 'app.contactsAndOrganizations.detail.editBankAccount'
                                        : 'app.contactsAndOrganizations.detail.addBankAccount'
                                }
                            />
                        }
                        isVisible={!!bankAccount}
                        actionChildren={actionChildren}
                        handleClose={onClose}
                    >
                        <Grid container={true} spacing={5} className={classes.container}>
                            <Grid container={true} item={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <Field name="iban">
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                label={<FormattedMessage id="app.common.iban" />}
                                                fullWidth={true}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name="bic">
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                label={<FormattedMessage id="app.common.bic" />}
                                                fullWidth={true}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name="mandate">
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                label={<FormattedMessage id="app.common.mandate" />}
                                                fullWidth={true}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <FormikPoolDatePicker
                                        name="mandateDate"
                                        label={<FormattedMessage id="app.common.mandateDate" />}
                                    />
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name="customerReference">
                                        {({ field, meta }: FieldProps<string>) => (
                                            <PoolTextArea
                                                label={<FormattedMessage id="app.common.customerReference" />}
                                                fullWidth={true}
                                                rows={3}
                                                rowsMax={50}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                {...field}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                            <Grid container={true} item={true}>
                                <Grid item={true} xs={12}>
                                    <BankAccountsDocuments form={formikProps} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
